import React, { useState, useMemo, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser, faLock, faUserTie, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { faApple } from "@fortawesome/free-brands-svg-icons";
import { useTheme } from '../contexts/ThemeContext';
import authService from '../services/authService';
import { setReferralCode, getReferralCode } from '../utils/referralCodeStorage';
import './Signup.css';

const PENDING_VERIFY_KEY = 'examflip_pending_verify';
const PENDING_VERIFY_TTL_MS = 30 * 60 * 1000; // 30 minutes

function storePendingVerifyCredentials(email, password) {
  try {
    localStorage.setItem(PENDING_VERIFY_KEY, JSON.stringify({
      email,
      password,
      expires: Date.now() + PENDING_VERIFY_TTL_MS,
    }));
  } catch { /* storage unavailable */ }
}

// Predefined list of titles (mirrors ConfirmRegistration.js)
const USER_TITLES = [
  'Mr.', 'Mrs.', 'Ms.', 'Miss', 'Mx.', 'Dr.', 'Professor',
  'Associate Professor', 'Assistant Professor', 'Adjunct Professor',
  'Lecturer', 'Instructor', 'Faculty', 'Student Doctor', 'Medical Student',
  'Pre-Med Student', 'Resident Physician', 'Resident', 'Fellow', 'Intern',
  'Physician', 'Physician Assistant', 'Nurse Practitioner', 'Registered Nurse',
  'Clinical Pharmacist', 'Paramedic', 'EMT'
];

// step: 'method' | 'email-form'
function SignUpPage() {
  const [step, setStep] = useState('method');
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    title: '',
    password: '',
    confirmPassword: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [receivePromotions, setReceivePromotions] = useState(true);
  const [referralCode, setReferralCodeState] = useState("");
  const { isDarkMode } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  // Check for referral code in URL or localStorage on component mount
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const code = searchParams.get('code');
    if (code) {
      setReferralCodeState(code);
      setReferralCode(code);
    } else {
      const storedCode = getReferralCode();
      if (storedCode) {
        setReferralCodeState(storedCode);
      }
    }
  }, [location]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleOAuthLogin = (provider) => {
    setError('');

    const providerKey = provider.toLowerCase();
    const query = referralCode ? `?referralCode=${encodeURIComponent(referralCode)}` : '';

    window.location.href = `${process.env.REACT_APP_API_BASE_URL}/api/session/${providerKey}/start${query}`;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        throw new Error("Please enter a valid email address.");
      }
      if (!formData.firstName.trim()) throw new Error("First name is required.");
      if (!formData.lastName.trim()) throw new Error("Last name is required.");
      if (!formData.title) throw new Error("Please select a title.");
      if (formData.password.length < 8) throw new Error("Password must be at least 8 characters.");
      if (formData.password !== formData.confirmPassword) throw new Error("Passwords do not match.");
      if (!acceptedTerms) throw new Error("You must accept the Terms of Use to create an account.");

      const codeToSend = referralCode || null;

      const response = await authService.register(
        formData.email,
        codeToSend,
        receivePromotions,
        formData.firstName,
        formData.lastName,
        formData.title,
        formData.password
      );
      
      // Check if user already exists and is active (DuplicateUser)
      if (response && response.result === "DuplicateUser") {
        const signInUrl = referralCode ? `/?code=${referralCode}` : '/';
        const resetPasswordUrl = referralCode ? `/reset-password?code=${referralCode}` : '/reset-password';
        setError(
          <div style={{ textAlign: 'left' }}>
            <div style={{ marginBottom: '10px' }}>{response.message}</div>
            <div>
              <Link to={signInUrl} style={{ color: isDarkMode ? '#90cdf4' : '#3498db', textDecoration: 'underline', fontWeight: '500' }}>
                Click here to sign in
              </Link>
              {' or '}
              <Link to={resetPasswordUrl} style={{ color: isDarkMode ? '#90cdf4' : '#3498db', textDecoration: 'underline', fontWeight: '500' }}>
                reset your password
              </Link>
              {' if you forgot it.'}
            </div>
          </div>
        );
        return;
      }

      if (response && response.result === "UserPending") {
        storePendingVerifyCredentials(formData.email, formData.password);
        setError(
          <div style={{ textAlign: 'left' }}>
            <div style={{ marginBottom: '10px' }}>{response.message}</div>
            <button
              onClick={() => navigate(`/verify-email?email=${encodeURIComponent(formData.email)}`)}
              style={{ background: 'transparent', color: isDarkMode ? '#90cdf4' : '#3498db', border: 'none', textDecoration: 'underline', cursor: 'pointer', fontSize: '14px', padding: 0, fontWeight: '500' }}
            >
              Enter your verification code
            </button>
          </div>
        );
        return;
      }

      if (response && response.result === "Success") {
        storePendingVerifyCredentials(formData.email, formData.password);
        navigate(`/verify-email?email=${encodeURIComponent(formData.email)}`);
      } else {
        setError("Registration failed. Please try again.");
      }
    } catch (error) {
      if (error.status === 400 && error.data) {
        const errorDetail = error.data.detail || error.data.message || '';
        const errorTitle = error.data.title || '';
        if (errorDetail.toLowerCase().includes('referral code') || errorTitle.toLowerCase().includes('referral code')) {
          setError("The referral link you used is invalid or has expired. Please check the link and try again.");
        } else {
          setError(errorDetail || error.message || "Registration failed. Please try again.");
        }
      } else {
        setError(error.message || "Registration failed. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Theme-specific styles
  const themeStyles = useMemo(() => ({
    container: {
      background: isDarkMode
        ? 'linear-gradient(180deg, #0e1626 0%, #0b1220 100%)'
        : '#eceff4'
    },
    content: {
      background: isDarkMode
        ? 'linear-gradient(145deg, #151e30 0%, #121a2c 100%)'
        : 'linear-gradient(145deg, #ffffff 0%, #f5f9ff 100%)',
      boxShadow: isDarkMode ? '0 6px 12px rgba(0, 0, 0, 0.3)' : '0 6px 12px rgba(0, 0, 0, 0.1)'
    },
    text: {
      primary: isDarkMode ? '#ffffff' : '#2c3e50',
      secondary: isDarkMode ? '#e1e9ff' : 'rgba(44, 62, 80, 0.8)'
    },
    button: { background: isDarkMode ? '#0074cc' : '#1976d2', color: '#ffffff' },
    link: { color: isDarkMode ? '#90cdf4' : '#3498db' },
    select: {
      background: isDarkMode ? 'rgba(255,255,255,0.1)' : 'transparent',
      color: isDarkMode ? '#ffffff' : '#2c3e50',
      border: isDarkMode ? '1px solid rgba(255,255,255,0.3)' : '1px solid rgba(44,62,80,0.3)'
    }
  }), [isDarkMode]);

  // ── Method selection screen ──
  if (step === 'method') {
    return (
      <div className={`login-page-container ${isDarkMode ? 'dark-mode' : 'light-mode'}`} style={themeStyles.container}>
        <div className="login-container-main" style={themeStyles.content}>
          <div className="login-header">
            <img src={isDarkMode ? '/dark_green.png' : '/white_black.png'} alt="ExamFlip Logo" className="header-logo" />
          </div>

          <div style={{ textAlign: 'center', marginBottom: '24px' }}>
            <h2 style={{ color: themeStyles.text.primary, marginBottom: '8px' }}>Create your account</h2>
            <p style={{ color: themeStyles.text.secondary, fontSize: '14px' }}>Choose how you'd like to sign up</p>
          </div>

          {error && (
            <div className="error-message" style={{ color: '#e53e3e', marginBottom: '15px', padding: '12px', backgroundColor: isDarkMode ? 'rgba(229,62,62,0.1)' : 'rgba(229,62,62,0.05)', borderRadius: '8px', border: '1px solid rgba(229,62,62,0.3)', fontSize: '14px' }}>
              {error}
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '24px' }}>
            <button
              onClick={() => { setError(''); setStep('email-form'); }}
              className="login-button"
              style={{ background: themeStyles.button.background, color: themeStyles.button.color, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}
            >
              <FontAwesomeIcon icon={faUser} />
              Continue with Email
            </button>

            <button
              className="google-signin-button"
              onClick={() => handleOAuthLogin('Google')}
              type="button"
              style={{ width: '100%' }}
            >
              <svg className="google-icon" viewBox="0 0 24 24" width="18" height="18">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              <span>Continue with Google</span>
            </button>

            <button
              className="apple-signin-button"
              onClick={() => handleOAuthLogin('Apple')}
              type="button"
              style={{ width: '100%' }}
            >
              <FontAwesomeIcon icon={faApple} style={{ fontSize: '18px' }} />
              <span>Continue with Apple</span>
            </button>
          </div>

          <div className="register-now">
            <p style={{ color: themeStyles.text.primary }}>
              Already have an account?{' '}
              <Link to={referralCode ? `/?code=${referralCode}` : '/'} style={{ color: themeStyles.link.color }}>Sign in</Link>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ── Email registration form ──
  return (
    <div className={`login-page-container ${isDarkMode ? 'dark-mode' : 'light-mode'}`} style={themeStyles.container}>
      <div className="login-container-main" style={themeStyles.content}>
        <div className="login-header">
          <img src={isDarkMode ? '/dark_green.png' : '/white_black.png'} alt="ExamFlip Logo" className="header-logo" />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
          <button
            type="button"
            onClick={() => { setStep('method'); setError(''); }}
            style={{ background: 'none', border: 'none', color: themeStyles.link.color, cursor: 'pointer', fontSize: '14px', padding: 0, marginRight: '8px' }}
          >
            ← Back
          </button>
          <h3 style={{ color: themeStyles.text.primary, margin: 0, fontSize: '18px' }}>Create Account</h3>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          {/* Email */}
          <div className="input-group">
            <FontAwesomeIcon icon={faUser} className="auth-input-icon" style={{ color: themeStyles.text.primary }} />
            <input type="email" name="email" placeholder="Email" value={formData.email} onChange={handleChange} required className="auth-input" disabled={isLoading} />
          </div>

          {/* First Name */}
          <div className="input-group">
            <FontAwesomeIcon icon={faUser} className="auth-input-icon" style={{ color: themeStyles.text.primary }} />
            <input type="text" name="firstName" placeholder="First Name" value={formData.firstName} onChange={handleChange} required className="auth-input" disabled={isLoading} />
          </div>

          {/* Last Name */}
          <div className="input-group">
            <FontAwesomeIcon icon={faUser} className="auth-input-icon" style={{ color: themeStyles.text.primary }} />
            <input type="text" name="lastName" placeholder="Last Name" value={formData.lastName} onChange={handleChange} required className="auth-input" disabled={isLoading} />
          </div>

          {/* Title */}
          <div className="input-group">
            <FontAwesomeIcon icon={faUserTie} className="auth-input-icon" style={{ color: themeStyles.text.primary }} />
            <select name="title" value={formData.title} onChange={handleChange} required className="auth-input" disabled={isLoading} style={themeStyles.select}>
              <option value="">Select Title</option>
              {USER_TITLES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>

          {/* Password */}
          <div className="input-group">
            <FontAwesomeIcon icon={faLock} className="auth-input-icon" style={{ color: themeStyles.text.primary }} />
            <input type="password" name="password" placeholder="Password" value={formData.password} onChange={handleChange} required className="auth-input" disabled={isLoading} />
          </div>

          {/* Confirm Password */}
          <div className="input-group">
            <FontAwesomeIcon icon={faLock} className="auth-input-icon" style={{ color: themeStyles.text.primary }} />
            <input type="password" name="confirmPassword" placeholder="Confirm Password" value={formData.confirmPassword} onChange={handleChange} required className="auth-input" disabled={isLoading} />
          </div>

          {/* Terms */}
          <div className="terms-checkbox-container" style={{ marginBottom: '15px', display: 'flex', alignItems: 'flex-start', gap: '10px', fontSize: '14px', lineHeight: '1.4' }}>
            <input type="checkbox" id="terms-checkbox" checked={acceptedTerms} onChange={(e) => setAcceptedTerms(e.target.checked)} style={{ marginTop: '2px', cursor: 'pointer', minWidth: '16px', height: '16px' }} disabled={isLoading} />
            <label htmlFor="terms-checkbox" style={{ color: themeStyles.text.secondary, cursor: 'pointer', userSelect: 'none' }}>
              I have read and agree to the{' '}
              <a href="https://www.examflip.com/terms.html" target="_blank" rel="noopener noreferrer" style={{ color: themeStyles.link.color, textDecoration: 'underline' }}>Terms of Use</a>
              {' '}and{' '}
              <a href="https://www.examflip.com/privacy.html" target="_blank" rel="noopener noreferrer" style={{ color: themeStyles.link.color, textDecoration: 'underline' }}>Privacy Policy</a>
            </label>
          </div>

          {/* Promotions */}
          <div className="terms-checkbox-container" style={{ marginBottom: '20px', display: 'flex', alignItems: 'flex-start', gap: '10px', fontSize: '14px', lineHeight: '1.4' }}>
            <input type="checkbox" id="promotions-checkbox" checked={receivePromotions} onChange={(e) => setReceivePromotions(e.target.checked)} style={{ marginTop: '2px', cursor: 'pointer', minWidth: '16px', height: '16px' }} disabled={isLoading} />
            <label htmlFor="promotions-checkbox" style={{ color: themeStyles.text.secondary, cursor: 'pointer', userSelect: 'none' }}>
              I would like to receive updates on the latest products and promotions via email or other channels
            </label>
          </div>

          {error && (
            <div className="error-message" style={{ color: '#e53e3e', marginBottom: '15px', padding: '15px', backgroundColor: isDarkMode ? 'rgba(229,62,62,0.1)' : 'rgba(229,62,62,0.05)', borderRadius: '8px', border: '1px solid rgba(229,62,62,0.3)', lineHeight: '1.6', fontSize: '14px' }}>
              {error}
            </div>
          )}

          <button type="submit" className="login-button" disabled={isLoading || !acceptedTerms} style={{ background: (isLoading || !acceptedTerms) ? '#ccc' : themeStyles.button.background, color: themeStyles.button.color, cursor: (isLoading || !acceptedTerms) ? 'not-allowed' : 'pointer' }}>
            {isLoading ? (
              <><FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: '8px' }} />Creating account...</>
            ) : 'Create account'}
          </button>
        </form>

        <div className="register-now">
          <p style={{ color: themeStyles.text.primary }}>
            Already have an account?{' '}
            <Link to={referralCode ? `/?code=${referralCode}` : '/'} style={{ color: themeStyles.link.color }}>Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default SignUpPage;
